import { ChevronDown, ChevronRight, Edit2, User } from 'lucide-react';
import { InjuryBadge } from '../ui/InjuryBadge';
import { GameDetailsPlayerStatsSummary } from './GameDetailsPlayerStatsSummary';
import type { InjuryStatus } from '../../constants/injuries';
import type { Player, StatWithId } from '../../types';

/**
 * Per-game stats list for any sport that doesn't get its own dedicated view
 * (Basketball, Soccer, Volleyball, Hockey, Lacrosse, and — the reason this
 * exists as its own component (sc-276 D3) — Tennis, once "Matches" lands).
 * Football has its own stats tab; Baseball/Softball has its own lineup-ordered
 * view; every other sport renders this.
 */
interface GenericSportGameDetailProps {
  /** Already ordered: game lineup first (if any), then remaining roster. */
  players: Player[];
  /** For the batting/lineup order number shown next to each player, when available. */
  lineup: Array<{ order: number; player: Player | null }>;
  playerCards: Map<string, string>;
  playerStats: Map<string, StatWithId[]>;
  calculatedStats: Map<string, Record<string, number>>;
  expandedPlayerIds: Set<string>;
  sport?: string;
  onToggleExpand: (playerId: string) => void;
  onEditStats: (playerId: string, cardId: string) => void;
}

export function GenericSportGameDetail({
  players,
  lineup,
  playerCards,
  playerStats,
  calculatedStats,
  expandedPlayerIds,
  sport,
  onToggleExpand,
  onEditStats,
}: GenericSportGameDetailProps) {
  if (players.length === 0) {
    return <p className="text-sm text-gray-500">No players with player cards yet. Add player cards from Roster to record stats for this game.</p>;
  }

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">Click a player to add or edit statistics. Use the chevron to expand and view stats.</p>
      <div className="space-y-1">
        {players.map((player) => {
          const lineupSpot = lineup.find((e) => e.player?.id === player.id);
          const isExpanded = expandedPlayerIds.has(player.id);
          const cardId = playerCards.get(player.id);
          return (
            <div key={player.id} className="rounded border border-gray-200 bg-white overflow-hidden">
              <div
                className="flex items-center gap-2 p-2 w-full text-left cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={isExpanded ? 'Collapse player stats' : 'Expand player stats'}
                onClick={() => onToggleExpand(player.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onToggleExpand(player.id);
                  }
                }}
              >
                <span className="flex-shrink-0 w-6 text-sm font-semibold text-gray-500 tabular-nums">
                  {lineupSpot ? `${lineupSpot.order}.` : '—'}
                </span>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {player.number != null ? (
                    <span className="font-bold text-primary text-sm min-w-[2rem]">#{player.number}</span>
                  ) : (
                    <User size={16} className="text-gray-400 flex-shrink-0" />
                  )}
                  <span className="font-medium text-sm text-gray-900">
                    {player.first_name ?? ''} {player.last_name ?? ''}
                  </span>
                  {player.injury_status && player.injury_status !== 'H' && (
                    <InjuryBadge injuryStatus={player.injury_status as InjuryStatus} size="xs" showLabel={false} />
                  )}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleExpand(player.id);
                  }}
                  className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-primary transition-colors flex-shrink-0"
                  aria-label={isExpanded ? 'Collapse stats' : 'Expand stats'}
                >
                  {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </button>
                {cardId && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditStats(player.id, cardId);
                    }}
                    className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-primary transition-colors flex-shrink-0"
                    aria-label="Edit stats"
                  >
                    <Edit2 size={16} />
                  </button>
                )}
              </div>
              {isExpanded && (
                <div className="px-2 pb-2 pt-0 border-t border-gray-100 bg-gray-50/80">
                  <GameDetailsPlayerStatsSummary
                    playerId={player.id}
                    rawStats={playerStats.get(player.id) || []}
                    calculated={calculatedStats.get(player.id) || {}}
                    sport={sport}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
